import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';
import { CheckCircle, ArrowLeft } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrialClass() {
  const { t } = useTranslation();
  const experienceLevels = [
    { value: 'nenhuma', label: t('register.iniciante') === 'Beginner' ? 'No experience' : 'Nenhuma experiência' },
    { value: 'iniciante', label: t('register.iniciante') },
    { value: 'intermediário', label: t('register.intermediario') }
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '08:00',
    experience_level: 'nenhuma',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/trial-classes`, formData);
      setSuccess(true);
      toast.success(t('trial.successTitle'));
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('trial.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="fixed top-4 right-4 z-50"><LanguageToggle /></div>
        <div className="max-w-md w-full text-center">
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8">
            <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-white" weight="fill" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] mb-3">
              {t('trial.successTitle')}
            </h1>
            <p className="text-[#73706A] mb-6">{t('trial.successBody')}</p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white" data-testid="trial-goto-login">
                  {t('common.doLogin')}
                </Button>
              </Link>
              <button
                onClick={() => { setSuccess(false); setFormData({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '08:00', experience_level: 'nenhuma', notes: '' }); }}
                className="text-sm text-[#0077B6] hover:text-[#03045E]"
                data-testid="trial-new-request"
              >
                {t('common.newRegistration')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-white border-b border-[#E6E2D8] px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-tk.png" alt="Logo" className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display='none'; }} />
            <span className="font-['Outfit'] font-bold text-[#1A1A1A]">Tia Karol Sports</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login" className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium" data-testid="trial-login-link">
              {t('common.doLogin')}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#73706A] hover:text-[#1A1A1A] mb-6" data-testid="trial-back-link">
          <ArrowLeft size={16} /> {t('common.back')}
        </Link>

        <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden">
          <div
            className="h-48 bg-cover bg-center relative"
            style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1596489396342-9988b49dbbba?q=80&w=2832&auto=format&fit=crop)' }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20" />
            <div className="absolute bottom-0 left-0 p-6 text-white">
              <h1 className="font-['Outfit'] text-3xl font-bold">{t('trial.title')}</h1>
              <p className="text-white/80 mt-1">{t('trial.subtitle')}</p>
            </div>
          </div>

          <div className="p-6">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-[#9B2226] text-sm" data-testid="trial-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>{t('common.fullName')} *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('common.yourName')} required data-testid="trial-name-input" />
                </div>
                <div>
                  <Label>{t('common.email')} *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={t('login.emailPlaceholder')} required data-testid="trial-email-input" />
                </div>
                <div>
                  <Label>{t('common.phone')} *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(000) 000-0000" required data-testid="trial-phone-input" />
                </div>
                <div>
                  <Label>{t('trial.preferredDate')} *</Label>
                  <Input type="date" value={formData.preferred_date} onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })} required data-testid="trial-date-input" />
                </div>
                <div>
                  <Label>{t('common.preferredDate')} *</Label>
                  <Input type="time" value={formData.preferred_time} onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })} required data-testid="trial-time-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label>{t('register.selectCategory')}</Label>
                  <Select value={formData.experience_level} onValueChange={(value) => setFormData({ ...formData, experience_level: value })}>
                    <SelectTrigger data-testid="trial-level-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {experienceLevels.map(level => (
                        <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>{t('common.notes')}</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input-field min-h-[80px] resize-none"
                    placeholder={t('trial.notesPh')}
                    data-testid="trial-notes-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white py-3" data-testid="trial-submit-btn">
                {loading ? t('trial.submitting') : t('trial.submit')}
              </Button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
